import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthProvider, useSignOut } from "@/context/AuthProvider";
import { ME_QUERY_KEY, RbacProvider } from "@/context/RbacContext";
import { SessionExpiredProvider } from "@/context/SessionExpiredContext";
import { reportIfSessionExpired } from "@/context/sessionExpiredSignal";
import { NotifierProvider } from "@/components/ui";
import { SignIn } from "@/features/auth/SignIn";
import { NoAccess } from "@/features/auth/NoAccess";
import { AcceptInvite } from "@/features/auth/AcceptInvite";
import { router } from "@/routes/router";

/**
 * Provider composition (outer → inner):
 *   QueryClient → Auth (token getter) → Notifier → RBAC (/me) → Router.
 * RBAC loads /me ONCE and gates the app on a resolved identity; an auth/roster failure renders the
 * error state instead of a half-loaded shell.
 */
const queryClient = new QueryClient({
  // A 401 anywhere (a query OR a mutation) means the Auth0 session is dead: raise the session-expired
  // overlay from one choke point instead of letting each of ~25 ErrorState call sites render a
  // dead-end "Something went wrong" whose Retry just re-fails. reportIfSessionExpired is strictly
  // gated on status===401, so 403/404/network/500 and the sync-now envelopes are untouched.
  queryCache: new QueryCache({ onError: (error) => void reportIfSessionExpired(error) }),
  mutationCache: new MutationCache({ onError: (error) => void reportIfSessionExpired(error) }),
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: (failureCount, error) => {
        // Never retry auth/permission failures — surface them immediately.
        const status = (error as { error?: { status?: number } })?.error?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

function AuthGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <AuthProvider
      loading={<CenteredMessage message={t("auth.signingIn")} />}
      unauthenticated={(login) => <SignIn onSignIn={login} />}
    >
      {children}
    </AuthProvider>
  );
}

function CenteredMessage({ message }: { message: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-(--bg) text-(--text-muted)">
      <p className="text-(length:--text-sm)">{message}</p>
    </div>
  );
}

/**
 * The RBAC gate's error branch. A 403 here means the token is valid but the user isn't on the staff
 * roster (the backend Stage-2 gate) — we render the no-access screen with sign-out, not a retry.
 * Transport errors get a retry that refetches /me.
 */
function GateError({ error }: { error: unknown }) {
  const signOut = useSignOut();
  return (
    <NoAccess
      error={error}
      onSignOut={signOut}
      onRetry={() => void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY })}
    />
  );
}

export function App() {
  const { t } = useTranslation();

  // PUBLIC route: the accept-invite screen runs pre-auth (it owns its own Auth0 context), so it must
  // render OUTSIDE the AuthGate/RbacProvider. A plain pathname check (not the router) keeps it above the
  // provider stack. Auth0's redirect callback lands on "/" — never here — so this stays invite-only.
  if (typeof window !== "undefined" && window.location.pathname === "/accept-invite") {
    return <AcceptInvite />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AuthGate>
        {/* Inside AuthGate (Auth0 context available) + under QueryClientProvider: the overlay can call
            loginWithRedirect/logout and invalidate queries. Mounted above the app so a 401 on ANY
            screen raises it. */}
        <SessionExpiredProvider>
          <NotifierProvider>
            <RbacProvider
              fallback={<CenteredMessage message={t("auth.loadingProfile")} />}
              onError={(error) => <GateError error={error} />}
            >
              <RouterProvider router={router} />
            </RbacProvider>
          </NotifierProvider>
        </SessionExpiredProvider>
      </AuthGate>
    </QueryClientProvider>
  );
}
